import React, { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useLocation } from 'react-router-dom';

// Layout
import Layout from 'layouts/settings/Layout';
import ColoredTagSelection from 'components/Tags/ColoredTagSelectionGeneral';
import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';
import MDButton from 'components/MDButton';
import Grid from '@mui/material/Grid';
import Card from '@mui/material/Card';
import Divider from '@mui/material/Divider';
import Dialog from '@mui/material/Dialog';
import AaroLoadingScreen from 'components/Loading';
import { config } from 'Constants';

// Tag management components
import EditTagGroups from 'components/Tags/Forms/EditTagGroups';
import EditTags from 'components/Tags/Forms/EditTags';

// API
import { getAllTagGroups } from 'services/api/general/tagGroup';

function useQueryParams() {
    return new URLSearchParams(useLocation().search);
}

// Fetch all tag groups
const fetchAllTagGroups = async () => {
    const response = await getAllTagGroups().then((rp) => (rp.data?.result ? rp.data?.result : []));
    return response;
};

export default function Settings() {
    const queryParams = useQueryParams();
    const tagGroupParam = queryParams.get('tagGroup');
    const [selectedTagGroup, setSelectedTagGroup] = useState(null);
    const [openEditDialog, setOpenEditDialog] = useState(false);

    const {
        data: tagGroups,
        isLoading,
        error,
    } = useQuery({
        queryKey: ['allTagGroups'],
        queryFn: () => fetchAllTagGroups(),
        staleTime: 60000,
    });

    useEffect(() => {
        if (tagGroupParam && tagGroups) {
            const foundTagGroup = tagGroups.find((group) => group._id === tagGroupParam);
            if (foundTagGroup) {
                setSelectedTagGroup(foundTagGroup);
                setOpenEditDialog(true);
            }
        }
    }, [tagGroupParam, tagGroups]);

    if (isLoading) return <AaroLoadingScreen />;
    if (error) return 'Bir Hata Oluştu: ' + error.message;

    return (
        <Layout>
            <MDBox mb={4}>
                <MDTypography variant="h4" fontWeight="medium">
                    Tag Grupları Yönetimi
                </MDTypography>
            </MDBox>
            <Card sx={{ p: 3, mb: 4 }}>
                <EditTagGroups />
            </Card>
            <MDBox mb={3}>
                <MDTypography variant="h4" fontWeight="medium">
                    Sık Kullanılan Tag Grupları
                </MDTypography>
                <MDTypography variant="body2" color="text">
                    Burada bazı önemli tag gruplarını görebilir ve yönetebilirsiniz.
                </MDTypography>
            </MDBox>
            <Grid container spacing={3}>
                {/* Fixed tag groups */}
                <Grid
                    size={{
                        xs: 12,
                        md: 6,
                        lg: 4,
                    }}
                >
                    <MDBox p={3} borderRadius="lg" shadow="lg" bgColor="white">
                        <MDBox>
                            <ColoredTagSelection
                                tagGroupID={config.fixedID.DEMAND_TEMPLATE_CATEGORY_ID}
                                tagLabel="Şablon Kategorileri"
                            />
                        </MDBox>
                        <Divider sx={{ my: 2 }} />
                        <MDBox display="flex" justifyContent="flex-end">
                            <MDButton
                                variant="outlined"
                                color="info"
                                size="small"
                                onClick={() => {
                                    setSelectedTagGroup({ _id: config.fixedID.DEMAND_TEMPLATE_CATEGORY_ID });
                                    setOpenEditDialog(true);
                                }}
                            >
                                Etiketleri Düzenle
                            </MDButton>
                        </MDBox>
                    </MDBox>
                </Grid>

                <Grid
                    size={{
                        xs: 12,
                        md: 6,
                        lg: 4,
                    }}
                >
                    <MDBox p={3} borderRadius="lg" shadow="lg" bgColor="white">
                        <MDBox>
                            <ColoredTagSelection
                                tagGroupID={config.fixedID.DEMAND_TEMPLATE_PRODUCT_ID}
                                tagLabel="Şablon Kapı Tagleri"
                            />
                        </MDBox>
                        <Divider sx={{ my: 2 }} />
                        <MDBox display="flex" justifyContent="flex-end">
                            <MDButton
                                variant="outlined"
                                color="info"
                                size="small"
                                onClick={() => {
                                    setSelectedTagGroup({ _id: config.fixedID.DEMAND_TEMPLATE_PRODUCT_ID });
                                    setOpenEditDialog(true);
                                }}
                            >
                                Etiketleri Düzenle
                            </MDButton>
                        </MDBox>
                    </MDBox>
                </Grid>

                {/* Dynamic tag groups */}
                {tagGroups.map((tagGroup) =>
                    // Skip the ones we've already shown specifically
                    tagGroup._id === config.fixedID.DEMAND_TEMPLATE_CATEGORY_ID ||
                    tagGroup._id === config.fixedID.DEMAND_TEMPLATE_PRODUCT_ID ? null : (
                        <Grid
                            key={tagGroup._id}
                            size={{
                                xs: 12,
                                md: 6,
                                lg: 4,
                            }}
                        >
                            <MDBox p={3} borderRadius="lg" shadow="lg" bgColor="white">
                                <MDBox>
                                    <ColoredTagSelection tagGroupID={tagGroup._id} tagLabel={tagGroup.name} />
                                </MDBox>
                                <Divider sx={{ my: 2 }} />
                                <MDBox display="flex" justifyContent="flex-end">
                                    <MDButton
                                        variant="outlined"
                                        color="info"
                                        size="small"
                                        onClick={() => {
                                            setSelectedTagGroup(tagGroup);
                                            setOpenEditDialog(true);
                                        }}
                                    >
                                        Etiketleri Düzenle
                                    </MDButton>
                                </MDBox>
                            </MDBox>
                        </Grid>
                    )
                )}
            </Grid>
            {/* Dialog for editing tags in a specific tag group */}
            <Dialog open={openEditDialog} onClose={() => setOpenEditDialog(false)} fullWidth maxWidth="md">
                <MDBox p={3}>
                    <MDTypography variant="h6" gutterBottom>
                        {selectedTagGroup
                            ? `"${tagGroups.find((g) => g._id === selectedTagGroup._id)?.name}" Etiketlerini Düzenle`
                            : 'Etiketleri Düzenle'}
                    </MDTypography>
                    <EditTags tagGroupID={selectedTagGroup?._id} />
                    <MDBox display="flex" justifyContent="flex-end" mt={2}>
                        <MDButton onClick={() => setOpenEditDialog(false)}>Kapat</MDButton>
                    </MDBox>
                </MDBox>
            </Dialog>
        </Layout>
    );
}
